import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CheckCircle2, Ban } from "lucide-react";
import { supabase } from "@/lib/supabase/supabaseClient";
import { UserRegisterForm } from "@/components/userRegisterForm";
import { UserLoginForm } from "@/components/userLoginForm";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import LoadingSpinner from "@/components/LoadingSpinner";
import { toast } from "sonner";
import { useSessionInit } from "@/lib/states/session/hooks";
import { store } from "@/lib/states/store";
import type { User } from "@supabase/supabase-js";
import { signInWithGoogle } from "@/auth/signInWithGoogle";
import { trpc } from "@/lib/trpc/client";
import { trpcVanilla, trpcUtils } from "@/lib/trpc/vanilla";

type InvitePreviewResponse = Awaited<
  ReturnType<(typeof trpcVanilla)["team"]["previewInvite"]["query"]>
>;

export const Route = createFileRoute("/join")({
  component: JoinPage,
  beforeLoad: async ({ search }) => {
    const token = typeof search.token === "string" ? search.token : "";
    if (!token) return;

    const preview = await trpcUtils.team.previewInvite.ensureData({ token });
    if (!preview.valid || !preview.organisationId) return;

    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.user) return;

    const authSession = await trpcVanilla.auth.getSession.query({
      orgId: preview.organisationId,
    });
    if (
      authSession.memberships.some(
        (membership) => membership.orgId === preview.organisationId,
      )
    ) {
      await store.getState().session.initWithUserAsync(session.user, {
        force: true,
        orgId: preview.organisationId,
      });
      throw redirect({ to: "/clients", replace: true });
    }
  },
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
});

function JoinPage() {
  const { token } = Route.useSearch();
  const [activeTab, setActiveTab] = useState("create");
  const init = useSessionInit();
  const navigate = useNavigate();
  const redeemInviteMutation = trpc.team.redeemInvite.useMutation();
  const [sessionUser, setSessionUser] = useState<User | null>(null);
  const [joinSuccess, setJoinSuccess] = useState(false);
  const [joinLoading, setJoinLoading] = useState(false);
  const [inviteSnapshot, setInviteSnapshot] =
    useState<InvitePreviewResponse | null>(null);

  const previewQuery = trpc.team.previewInvite.useQuery(
    { token },
    { enabled: !!token },
  );

  const invite = previewQuery.data;
  const displayInvite = inviteSnapshot ?? invite;

  useEffect(() => {
    if (!token) {
      toast.error("Invite token missing");
    }
  }, [token]);

  useEffect(() => {
    if (invite?.valid) {
      setInviteSnapshot(invite);
    }
  }, [invite]);

  useEffect(() => {
    const loadSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSessionUser(data.session?.user ?? null);
    };
    loadSession();
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSessionUser(session?.user ?? null);
      },
    );
    return () => {
      listener?.subscription?.unsubscribe();
    };
  }, []);

  const inviteEmail =
    displayInvite && displayInvite.valid ? displayInvite.invitedEmail : "";
  const sessionEmail = sessionUser?.email?.trim().toLowerCase() ?? "";
  const normalisedInviteEmail = inviteEmail.trim().toLowerCase();
  const isSessionEmailMatchInvite =
    !!sessionEmail &&
    !!normalisedInviteEmail &&
    sessionEmail === normalisedInviteEmail;
  const orgAccent =
    displayInvite &&
    displayInvite.valid &&
    displayInvite.primaryColor &&
    /^#[0-9a-fA-F]{6}$/.test(displayInvite.primaryColor)
      ? displayInvite.primaryColor
      : undefined;

  const redeemInvite = async (inviteToken: string) => {
    const data = await redeemInviteMutation.mutateAsync({ token: inviteToken });
    return data;
  };

  const handleRedeemSuccess = async (user: User, organisationId: string) => {
    await init(
      user,
      () => {
        setJoinSuccess(true);
      },
      { force: true, orgId: organisationId },
    );
  };

  const handleVerifiedJoin = async (user: User) => {
    if (!invite || !invite.valid) {
      throw new Error("Invite is invalid or expired.");
    }

    if (user.email?.trim().toLowerCase() !== inviteEmail.toLowerCase()) {
      await supabase.auth.signOut();
      store.getState().session.reset();
      throw new Error("Signed in email does not match the invite.");
    }

    const data = await redeemInvite(token);
    await handleRedeemSuccess(user, data.organisationId);
  };

  const handleGoogleJoinAuth = async () => {
    if (!token) {
      throw new Error("Invite token missing");
    }

    await signInWithGoogle({
      redirectTo: `${window.location.origin}/join?token=${encodeURIComponent(
        token,
      )}`,
    });
  };

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-slate-50 p-6 md:p-10">
      <div className="flex w-full max-w-md flex-col gap-6">
        <a href="#" className="flex items-center gap-2 self-center font-medium">
          <img
            src="/lockupBlack.svg"
            alt="Trainrr"
            className="h-6 [filter:brightness(0)_saturate(100%)_invert(20%)_sepia(98%)_saturate(3828%)_hue-rotate(219deg)_brightness(103%)_contrast(104%)]"
          />
        </a>

        {previewQuery.isLoading && (
          <Card>
            <CardHeader>
              <CardTitle>Checking your invite...</CardTitle>
              <p className="text-sm text-muted-foreground">
                This will only take a moment.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-center">
                <div className="h-14 w-14 animate-spin rounded-full border-4 border-muted border-t-primary" />
              </div>
              <div className="space-y-3">
                <div className="h-4 w-full animate-pulse rounded-full bg-muted/70" />
                <div className="grid grid-cols-2 gap-3">
                  <div className="h-4 animate-pulse rounded-full bg-muted/70" />
                  <div className="h-4 animate-pulse rounded-full bg-muted/70" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!previewQuery.isLoading &&
          (!displayInvite || !displayInvite.valid) &&
          !joinSuccess && (
            <>
              <Card>
                <CardHeader className="text-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-50">
                      <Ban className="h-6 w-6 text-orange-500" />
                    </div>
                    <div className="space-y-2">
                      <CardTitle className="text-xl">
                        Invitation Link Expired
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        For security reasons, invitation links expire after 7
                        days. Please reach out to your team administrator to
                        request a new link.
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 flex flex-row items-center justify-center">
                  <Button
                    variant={"secondary"}
                    onClick={() => navigate({ to: "/" })}
                  >
                    Go to Homepage
                  </Button>
                </CardContent>
              </Card>

              <div className="text-center text-xs text-muted-foreground">
                Having trouble? Email us at{" "}
                <a
                  className="underline underline-offset-4"
                  href="mailto:support@trainrr.app"
                >
                  support@trainrr.app
                </a>
              </div>
            </>
          )}

        {displayInvite?.valid && joinSuccess && (
          <>
            <Card>
              <CardHeader className="text-center">
                <div className="flex flex-col items-center gap-3">
                  <CheckCircle2 className="size-32 text-emerald-500" />
                  <div className="space-y-1">
                    <CardTitle className="text-xl">
                      Welcome to the Team!
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      You are now part of {displayInvite.orgName}.
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  className="w-full text-white"
                  style={{ backgroundColor: orgAccent }}
                  onClick={async () => {
                    const organisationId = displayInvite.organisationId;
                    const {
                      data: { user },
                    } = await supabase.auth.getUser();
                    if (user && organisationId) {
                      await init(user, undefined, {
                        force: true,
                        orgId: organisationId,
                      });
                    }
                    navigate({ to: "/clients" });
                  }}
                >
                  Get started
                </Button>
              </CardContent>
            </Card>

            <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4">
              By continuing, you agree to our{" "}
              <a
                className="hover:text-primary"
                href="https://trainrr.app/terms"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                className="hover:text-primary"
                href="https://trainrr.app/privacy"
              >
                Privacy Policy
              </a>
              .
            </div>
          </>
        )}

        {displayInvite?.valid && sessionUser && !joinSuccess && (
          <>
            <Card>
              <CardHeader className="text-center">
                <div className="flex flex-col items-center gap-3">
                  {displayInvite.logoUrl ? (
                    <img
                      src={displayInvite.logoUrl}
                      alt={`${displayInvite.orgName} logo`}
                      className="h-14 w-14 rounded-2xl object-cover shadow-md"
                    />
                  ) : null}
                  <div className="space-y-1">
                    <CardTitle className="text-xl">
                      Join {displayInvite.orgName}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      You&apos;re signed in as {sessionUser.email}.
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  className="w-full text-white"
                  style={{ backgroundColor: orgAccent }}
                  disabled={joinLoading || !isSessionEmailMatchInvite}
                  onClick={async () => {
                    setJoinLoading(true);
                    try {
                      if (!isSessionEmailMatchInvite) {
                        throw new Error(
                          "Signed in email does not match the invited email.",
                        );
                      }
                      const data = await redeemInvite(token);
                      await handleRedeemSuccess(
                        sessionUser,
                        data.organisationId,
                      );
                    } catch (error) {
                      const message =
                        error instanceof Error
                          ? error.message
                          : "Unable to accept invite.";
                      toast.error(message);
                    } finally {
                      setJoinLoading(false);
                    }
                  }}
                >
                  Join Team
                  {joinLoading && <LoadingSpinner className="ml-2" />}
                </Button>
                {!isSessionEmailMatchInvite && (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                    This invite is for{" "}
                    <span className="font-medium">{inviteEmail}</span>. Sign out
                    and continue with that email to join this team.
                  </div>
                )}
                <div className="text-center text-sm text-muted-foreground">
                  Not you?{" "}
                  <button
                    type="button"
                    className="underline underline-offset-4"
                    onClick={async () => {
                      await supabase.auth.signOut();
                      store.getState().session.reset();
                    }}
                  >
                    Sign out
                  </button>
                </div>
              </CardContent>
            </Card>

            <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4">
              By continuing, you agree to our{" "}
              <a
                className="hover:text-primary"
                href="https://trainrr.app/terms"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                className="hover:text-primary"
                href="https://trainrr.app/privacy"
              >
                Privacy Policy
              </a>
              .
            </div>
          </>
        )}

        {displayInvite?.valid &&
          !sessionUser &&
          !joinSuccess && (
            <>
              <Tabs value={activeTab}>
                <TabsContent value="create">
                  <UserRegisterForm
                    defaultEmail={inviteEmail}
                    disableEmail
                    showLockedEmail
                    ctaColor={orgAccent}
                    onVerified={handleVerifiedJoin}
                    hideCaptcha
                    cardTitle={`Join ${displayInvite.orgName}`}
                    cardDescription="Complete your details and verify your email to join the team"
                    logoUrl={displayInvite.logoUrl ?? null}
                    ctaLabel="Send join code"
                    footerText="Already have an account?"
                    footerLinkLabel="Log in"
                    onFooterLinkClick={() => setActiveTab("login")}
                    onGoogleAuth={handleGoogleJoinAuth}
                  />
                </TabsContent>
                <TabsContent value="login">
                  <UserLoginForm
                    defaultEmail={inviteEmail}
                    disableEmail
                    showLockedEmail
                    ctaColor={orgAccent}
                    onVerified={handleVerifiedJoin}
                    cardTitle={`Join ${displayInvite.orgName}`}
                    cardDescription="Verify your email to join the team"
                    logoUrl={displayInvite.logoUrl ?? null}
                    ctaLabel="Send join code"
                    footerText="Don't have an account?"
                    footerLinkLabel="Sign up"
                    onFooterLinkClick={() => setActiveTab("create")}
                    onGoogleAuth={handleGoogleJoinAuth}
                  />
                </TabsContent>
              </Tabs>

              <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4">
                By continuing, you agree to our{" "}
                <a
                  className="hover:text-primary"
                  href="https://trainrr.app/terms"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  className="hover:text-primary"
                  href="https://trainrr.app/privacy"
                >
                  Privacy Policy
                </a>
                .
              </div>
            </>
          )}
      </div>
    </div>
  );
}
